import React from 'react';
import Icon from '../../../components/AppIcon';
import { getTranslation } from '../../../utils/translations';

const MarketAnalyticsSection = ({ countries, language }) => {
  const t = getTranslation(language);

  const activeMarkets = countries?.filter(c => c?.isActive)?.length || 0;
  const totalMarkets = countries?.length || 0;
  const regions = [...new Set(countries?.map(c => c?.region))]?.length || 0;
  const currencies = [...new Set(countries?.map(c => c?.currencyCode))]?.length || 0;

  const stats = [
    {
      label: language === 'FR' ? 'Marchés Actifs' : language === 'PL' ? 'Aktywne Rynki' : 'Active Markets',
      value: activeMarkets,
      total: totalMarkets,
      icon: 'Globe',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      label: language === 'FR' ? 'Régions' : language === 'PL' ? 'Regiony' : 'Regions',
      value: regions,
      icon: 'Map',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      label: language === 'FR' ? 'Devises' : language === 'PL' ? 'Waluty' : 'Currencies',
      value: currencies,
      icon: 'DollarSign',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      label: language === 'FR' ? 'Taux de TVA Moyen' : language === 'PL' ? 'Średnia Stawka Podatku' : 'Average Tax Rate',
      value: countries?.length > 0 ? (countries?.reduce((sum, c) => sum + (c?.taxRate || 0), 0) / countries?.length)?.toFixed(1) + '%' : '0%',
      icon: 'Percent',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  const regionDistribution = countries?.reduce((acc, country) => {
    acc[country?.region] = (acc?.[country?.region] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats?.map((stat, idx) => (
          <div key={idx} className="bg-background border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${stat?.bgColor}`}>
                <Icon name={stat?.icon} size={24} className={stat?.color} />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {stat?.value}{stat?.total ? `/${stat?.total}` : ''}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-background border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground text-lg mb-4">
          {language === 'FR' && 'Distribution par Région'}
          {language === 'EN' && 'Distribution by Region'}
          {language === 'PL' && 'Dystrybucja według Regionów'}
        </h3>
        <div className="space-y-3">
          {Object.entries(regionDistribution || {})?.map(([region, count]) => {
            const percentage = totalMarkets > 0 ? ((count / totalMarkets) * 100)?.toFixed(0) : 0;
            return (
              <div key={region}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{region}</span>
                  <span className="text-sm text-muted-foreground">{count} {language === 'FR' ? 'pays' : language === 'PL' ? 'kraje' : 'countries'} ({percentage}%)</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-background border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground text-lg mb-4">
          {language === 'FR' && 'Modes de Paiement Disponibles'}
          {language === 'EN' && 'Available Payment Methods'}
          {language === 'PL' && 'Dostępne Metody Płatności'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[...new Set(countries?.flatMap(c => c?.paymentMethods || []))]?.map((method, idx) => (
            <div key={idx} className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Icon name="CreditCard" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground capitalize">{method?.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketAnalyticsSection;